import { create } from 'zustand';
import { useDatabaseStore } from './databaseStore';
import { useConnectionStore } from '../../connections/store/connectionStore';
import {
  getLimitedDatabase,
  isSameDatabaseName,
} from '../../connections/utils/connectionDatabaseLimit';
import {
  getDatabaseFromNodeId as getDbFromNodeId,
  getConnectionIdFromNodeId
} from '../../explorer/utils/nodeIdUtils';

/**
 * B3 — coerce a selected database back to a limited connection's configured
 * database. When the target connection has `limitToDatabase: true` and a
 * non-empty `database`, and the incoming database DIFFERS from it
 * (case-insensitive), the incoming value is replaced with the configured one;
 * a case-insensitively equal or empty/undefined incoming value passes through
 * untouched (so clearing the selection still works).
 *
 * Reads the connection store lazily at call time — the same
 * `getState()`-inside-a-function pattern `databasesService` uses. Because
 * `useConnectionStore` is only touched inside this function (never during
 * module evaluation), the existing `connectionStore → uiStore` import edge
 * stays safe: neither module uses the other at init time. UI scoping only,
 * never a query/security gate.
 */
function coerceToLimitedDatabase(
  connectionId: string | undefined,
  database: string | undefined,
): string | undefined {
  if (!connectionId) return database;
  const state = useConnectionStore.getState();
  const connection =
    state.connections?.find((c) => c.id === connectionId) ??
    state.activeConnections?.find((c) => c.id === connectionId)?.connection;
  const target = getLimitedDatabase(connection);
  if (!target) return database;
  // An empty/undefined incoming value passes through so clearing still works —
  // this is the one place the limit rule deliberately does NOT substitute the
  // target (unlike `coerceDatabaseToConnectionLimit`, which seeds it).
  if (!database?.trim()) return database;
  return isSameDatabaseName(database, target) ? database : target;
}

interface DatabaseUIState {
  // Selected elements
  selectedNodeId: string | undefined;
  selectedConnectionId: string | undefined;
  selectedDatabase: string | undefined;
  // Track which connection the selected database belongs to
  selectedDatabaseConnectionId: string | undefined;
  // Node to highlight with animation and scroll into view
  highlightedNodeId: string | undefined;

  // Actions
  setSelectedNodeId: (nodeId: string | undefined) => void;
  setSelectedConnectionId: (connectionId: string | undefined) => void;
  /**
   * Set the selected database. Pass `connectionId` to also stamp the paired
   * `selectedDatabaseConnectionId` — required whenever the database is set
   * OUTSIDE `setSelectedNodeId`'s id-based derivation (e.g. workspace-pin
   * rows, whose ids carry no database segment), otherwise paired-context
   * consumers (`getValidSelectedDatabase`) treat the selection as invalid.
   * When omitted, `selectedDatabaseConnectionId` is left untouched (existing
   * caller behavior).
   */
  setSelectedDatabase: (database: string | undefined, connectionId?: string) => void;
  setHighlightedNodeId: (nodeId: string | undefined) => void;
  clearHighlightedNodeId: () => void;
  isNodeFromConnection: (nodeId: string | undefined, connectionId: string) => boolean;
  getDatabaseFromNodeId: (nodeId: string | undefined) => string | undefined;
  /** Clear selections that reference a specific connection (call when connection is disconnected) */
  clearSelectionForConnection: (connectionId: string) => void;

  // Computed getters
  getValidSelectedDatabase: () => { database: string; connectionId: string } | undefined;
}

export const useDatabaseUIStore = create<DatabaseUIState>((set, get) => ({
  // Initial state
  selectedNodeId: undefined,
  selectedConnectionId: undefined,
  selectedDatabase: undefined,
  selectedDatabaseConnectionId: undefined,
  highlightedNodeId: undefined,

  // Actions
  setSelectedNodeId: (nodeId: string | undefined) => {
    if (!nodeId) {
      set({
        selectedNodeId: undefined,
        selectedConnectionId: undefined,
        selectedDatabase: undefined,
        selectedDatabaseConnectionId: undefined
      });
      return;
    }

    // Extract the connection ID from the node
    const nodeConnectionId = getConnectionIdFromNodeId(nodeId);

    // Extract the database from the node (if any)
    const database = get().getDatabaseFromNodeId(nodeId);

    // Update state atomically
    set({
      selectedNodeId: nodeId,
      selectedConnectionId: nodeConnectionId,
      // Only set database if it's from the current node's connection
      selectedDatabase: database,
      selectedDatabaseConnectionId: database ? nodeConnectionId : undefined
    });
  },

  setSelectedConnectionId: (connectionId: string | undefined) =>
    set({ selectedConnectionId: connectionId }),

  setSelectedDatabase: (database: string | undefined, connectionId?: string) => {
    // B3 — the connection whose limit governs this selection is the one being
    // passed, or (for existing callers that omit it) the one the current
    // selection is already paired with.
    const governingConnectionId = connectionId ?? get().selectedDatabaseConnectionId;
    const coerced = coerceToLimitedDatabase(governingConnectionId, database);
    // V5 — clearing the database selection also clears the owning connection
    // pairing, so a later connectionId-less `setSelectedDatabase` can't coerce
    // a fresh selection against a stale limited connection.
    if (!coerced?.trim()) {
      set({ selectedDatabase: undefined, selectedDatabaseConnectionId: undefined });
      return;
    }
    set(
      connectionId !== undefined
        ? { selectedDatabase: coerced, selectedDatabaseConnectionId: connectionId }
        : { selectedDatabase: coerced },
    );
  },

  setHighlightedNodeId: (nodeId: string | undefined) =>
    set({ highlightedNodeId: nodeId }),

  clearHighlightedNodeId: () =>
    set({ highlightedNodeId: undefined }),

  clearSelectionForConnection: (connectionId: string) => {
    const state = get();
    // Clear selections if they reference the disconnected connection
    if (state.selectedConnectionId === connectionId) {
      set({
        selectedNodeId: undefined,
        selectedConnectionId: undefined,
        selectedDatabase: undefined,
        selectedDatabaseConnectionId: undefined,
      });
    } else if (state.selectedDatabaseConnectionId === connectionId) {
      // Only the database selection references this connection
      set({
        selectedDatabase: undefined,
        selectedDatabaseConnectionId: undefined,
      });
    }
  },

  isNodeFromConnection: (nodeId: string | undefined, connectionId: string) => {
    if (!nodeId) return false;
    const nodeConnectionId = getConnectionIdFromNodeId(nodeId);
    return nodeConnectionId === connectionId;
  },

  getDatabaseFromNodeId: (nodeId: string | undefined) => {
    if (!nodeId) return undefined;

    // First try to extract database directly from the node ID
    const directDbName = getDbFromNodeId(nodeId);
    if (directDbName) {
      return directDbName;
    }

    // Get access to the tree data
    const treeData = useDatabaseStore.getState().treeData;

    // Helper function to find a node by ID
    const findNodeById = (id: string, nodes: any[]): any => {
      for (const node of nodes) {
        if (node.id === id) {
          return node;
        }

        if (node.children?.length) {
          const foundInChildren = findNodeById(id, node.children);
          if (foundInChildren) {
            return foundInChildren;
          }
        }
      }

      return undefined;
    };

    // Find the current node
    const node = findNodeById(nodeId, treeData);
    if (!node) return undefined;

    // If this is already a database node, extract the database name from metadata
    if (node.type === 'database' && node.metadata?.name) {
      return node.metadata.name;
    }

    // Otherwise, traverse up the tree to find a database ancestor
    let currentNode = node;
    while (currentNode && currentNode.parentId) {
      const parentNode = findNodeById(currentNode.parentId, treeData);
      if (!parentNode) break;

      // Check if the parent is a database node
      if (parentNode.type === 'database' && parentNode.metadata?.name) {
        return parentNode.metadata.name;
      }

      // Move up to the parent
      currentNode = parentNode;
    }

    return undefined;
  },

  // Get the selected database only if it's valid (belongs to the current connection)
  getValidSelectedDatabase: () => {
    const state = get();

    // Must have both a database and a connection for it
    if (!state.selectedDatabase || !state.selectedDatabaseConnectionId) {
      return undefined;
    }

    // The database connection must match the currently selected connection
    if (state.selectedDatabaseConnectionId !== state.selectedConnectionId) {
      return undefined;
    }

    return {
      database: state.selectedDatabase,
      connectionId: state.selectedDatabaseConnectionId
    };
  }
}));
